/**
 * Easter party invitations — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterPartyInvitationsPack: ToilePack = {
    slug: 'easter-party-invitations',
    category: 'season',
    name: 'Easter party invitations',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'hunt', label: 'Egg hunt', subject: 'A vertical invitation card lettered "EGG HUNT" over a basket and grass, a large blank panel for the details'},
        {key: 'brunch', label: 'Brunch', subject: 'A vertical invitation card lettered "EASTER BRUNCH" over a blossom wreath, a blank panel for date and place'},
        {key: 'kids', label: "Kids' party", subject: 'A vertical invitation card lettered "EASTER PARTY" with cartoon bunnies and chicks, a blank panel'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for an office party', 'as a save the date', 'with a rsvp line'],
    directionHint: 'try: for an office party',
    seo: {
        title: "Easter invitations: egg hunt, brunch, kids' party",
        metaDescription: "Nine Easter invitations — egg hunt, brunch, kids' party — greeting lettered, details blank, pastel, vintage or kids' style.",
        h1: "Easter invitation: egg hunt, brunch and kids' party in three styles",
        tagline: 'Nine cards, three gathering types, three styles each — greeting set, details panel left for you.',
        intro: "Three Easter invitations for three kinds of gathering: an egg hunt, a brunch, a kids' party. Each card carries its greeting already lettered and leaves the date, time and address panel blank. Drawn in a soft pastel look, a vintage two-tone finish, or a bright kids' style. Type a different greeting and the card redraws.",
        useCases: [
            'an Easter egg hunt invitation',
            'an Easter brunch gathering invite',
            "a kids' Easter party invitation",
            'a printable card to hand out or email',
        ],
        faq: [
            {q: 'Is there room to add my own date and address?', a: 'Yes — the details panel below the greeting is left blank on every card, written in by hand or added in an editor.'},
            {q: 'Can I change the greeting?', a: "Yes — type your own wording, like a child's name or a specific theme, and the greeting redraws in the same style."},
            {q: 'What size do these print at?', a: 'Each invitation is drawn at a standard card proportion, ready to print, cut, and hand out or scan to send.'},
            {q: "Is there a more grown-up version for a brunch, not a kids' party?", a: "The vintage set leans more grown-up for exactly that, while the kids' set stays bright and the pastel set sits in between."},
        ],
    },
}
